import { fieldRequiredMessage, incorrectDeviceIdMessage } from 'assets/messages/errorMessages';
import { Button } from 'components/UI/Button/Button';
import { StyledFormError } from 'components/UI/FormError/StyledFormError';
import { StyledTextInput } from 'components/UI/InputElements/StyledTextInput';
import { StyledTextLabel } from 'components/UI/TextLabel/StyledTextLabel';
import React, { FormEventHandler } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { setHours, setMinutes } from 'date-fns';
import { Control, Controller, FieldError, UseFormRegister } from 'react-hook-form';
import { autofillG4ComboardId } from 'utils/formsUtils/formsUtils';
import { StyledTextInputTime } from './styled';

export interface FormInput {
  deviceId: string;
  dateRange: DateRange;
}

export interface FormErrors {
  deviceId?: FieldError;
  startTime?: FieldError;
  endTime?: FieldError;
  dateRange?: {
    from?: FieldError;
    to?: FieldError;
  };
}

interface Props {
  onSubmit: FormEventHandler<HTMLFormElement>;
  register: UseFormRegister<FormInput>;
  control: Control<FormInput>;
  errors: FormErrors;
  placeholder?: string;
}
export const FetchElasticSearchLogsForm = ({ onSubmit, register, errors, control }: Props) => {
  const [selected, setSelected] = React.useState<{
    startDate: Date;
    endDate: Date;
  }>();

  const [startTimeValue, setStartTimeValue] = React.useState<string>('00:00');
  const [endTimeValue, setEndTimeValue] = React.useState<string>('00:00');

  const handleDaySelect = (date: DateRange, field: { onChange: (date: DateRange) => void }) => {
    if (!startTimeValue || !endTimeValue || !date.from || !date.to) {
      return undefined;
    }
    const [startHours, startMinutes] = startTimeValue.split(':').map((str) => parseInt(str, 10));
    const [endHours, endMinutes] = endTimeValue.split(':').map((str) => parseInt(str, 10));

    const newStartDate = new Date(
      date?.from.getFullYear(),
      date?.from.getMonth(),
      date?.from.getDate(),
      startHours,
      startMinutes,
    );
    const newEndDate = new Date(
      date?.to.getFullYear(),
      date?.to.getMonth(),
      date?.to.getDate(),
      endHours,
      endMinutes,
    );

    const updatedSelection =
      date?.from && date?.to ? { startDate: newStartDate, endDate: newEndDate } : undefined;
    setSelected(updatedSelection);

    field.onChange({
      from: updatedSelection?.startDate,
      to: updatedSelection?.endDate,
    });

    return updatedSelection;
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { onChange: (value: { from: Date; to: Date }) => void },
    isTime: string,
  ) => {
    const isStartTime = isTime === 'start';
    const time = e.target.value;

    if (isStartTime) {
      setStartTimeValue(time);
    } else {
      setEndTimeValue(time);
    }
    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));

    if (selected?.endDate || selected?.startDate) {
      const currentDate = isStartTime ? selected?.startDate : selected?.endDate;
      const newTime = setHours(setMinutes(currentDate, minutes), hours);

      if (isStartTime) {
        setSelected({ ...selected, startDate: newTime });
        setStartTimeValue(time);
      } else {
        setSelected({ ...selected, endDate: newTime });
        setEndTimeValue(time);
      }

      field.onChange({
        from: isStartTime ? newTime : selected.startDate,
        to: !isStartTime ? newTime : selected.endDate,
      });
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <StyledTextLabel> Device Id:</StyledTextLabel>

      <StyledTextInput
        style={{ width: '93%' }}
        {...register('deviceId', {
          required: {
            value: true,
            message: fieldRequiredMessage,
          },
          setValueAs: autofillG4ComboardId(),
          pattern: {
            value: /^\d{9}-\d{9}$/,
            message: incorrectDeviceIdMessage,
          },
        })}
        placeholder="123456789-123456789"
      />
      {errors.deviceId && <StyledFormError>{errors.deviceId.message}</StyledFormError>}

      <Controller
        name="dateRange"
        control={control}
        rules={{
          validate: (dateRange) =>
            dateRange ? selected!!.startDate < selected!!.endDate : undefined,
        }}
        render={({ field }) => (
          <>
            <StyledTextLabel>Start Time: </StyledTextLabel>
            <StyledTextInputTime
              type="time"
              value={startTimeValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleTimeChange(e, field, 'start')
              }
            />
            <StyledTextLabel>End Time: </StyledTextLabel>
            <StyledTextInputTime
              type="time"
              value={endTimeValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleTimeChange(e, field, 'end')
              }
            />
            <DayPicker
              captionLayout="dropdown"
              styles={{
                day_button: {
                  margin: 0,
                },
                footer: {
                  textAlign: 'center',
                },
              }}
              mode="range"
              required
              selected={
                selected?.startDate && selected?.endDate
                  ? { from: selected.startDate, to: selected.endDate }
                  : undefined
              }
              onSelect={(dateRange) => {
                handleDaySelect(dateRange, field);
              }}
              footer={
                <div>
                  Selected date: <br />
                  {selected ? selected.startDate.toLocaleString('en-GB') : 'None'} -
                  {selected ? selected.endDate.toLocaleString('en-GB') : 'None'}
                </div>
              }
            />
            {errors.dateRange && (
              <StyledFormError>End date must be bigger than start date</StyledFormError>
            )}
          </>
        )}
      />

      <Button type="submit" widthPercent="70%">
        Send
      </Button>
    </form>
  );
};
